// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/common/backendFrontend/captionHelpersBe.ts

import type { CheerioAPI } from 'cheerio';
import type { TMap } from '@shared/types';
import { DOCUMENT_HTML_ATTRIBUTES } from '../../modules/documents/constants';
import { isValidCaptionKind, OutRefBookmarkKind } from '../../modules/documents/tiptap/nodes/caption/constants';
import { CheerioElement } from './wrapImagesForAlignFe';
import type { TDraft, TVersion } from '../../modules/documents/types/version.types';
import { ECustomFieldType } from '../../modules/users/types/organizations.types';
import { captionLabelMap } from '../i18n';
import { ZWSP } from '../../modules/documents/tiptap/plugins/zwsp';

export type TCaptionMapEntry = {
  index: number; // e.g., 1, 2, 3 - the sequential number for this caption kind
  content: string; // caption description text (may be empty)
  kind: OutRefBookmarkKind;
};
export type TCaptionMap = TMap<string, TCaptionMapEntry>;

/** Strip editor-only ZWSP placeholders from caption content. */
export const stripCaptionZwsp = (text: string): string => text.split(ZWSP).join('');

/** Normalize caption content for semantic consumers (maps, dialogs, refs). */
export const normalizeCaptionText = (text: string): string => stripCaptionZwsp(text).trim();

/** Compute prefix on-demand from entry data and label resolver */
export const getCaptionPrefix = (entry: TCaptionMapEntry, resolveLabel: (kind: OutRefBookmarkKind) => string): string =>
  `${resolveLabel(entry.kind)} ${entry.index}`;

/** Helper to get full caption text for display in dialogs */
export const getCaptionFullText = (
  entry: TCaptionMapEntry,
  resolveLabel: (kind: OutRefBookmarkKind) => string
): string => {
  const prefix = getCaptionPrefix(entry, resolveLabel);
  return entry.content ? `${prefix}: ${entry.content}` : prefix;
};

/**
 * collect bookmark-id → caption entry map from a cheerio root
 */
export const buildCaptionsMapFe = ($: CheerioAPI): TCaptionMap => {
  const map: TCaptionMap = {};
  $('caption-node').each((_, el) => {
    const $cap = $(el);
    const id = $cap.attr(DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID);
    if (!id) return;
    const kind = $cap.attr('data-bookmark-kind');
    if (!isValidCaptionKind(kind)) return;
    const index = parseInt($cap.attr('data-index') || '0', 10);
    const content = normalizeCaptionText($cap.text());
    map[id] = { index, content, kind };
  });
  return map;
};

/**
 * Build the canonical prefix string (no trailing space; colon at the end).
 * Examples: "Figure 3:", "Table 2:", "Figure:" (if index<=0 or missing)
 */
export const computePrefixFe = (
  resolveLabel?: (kind: OutRefBookmarkKind) => string,
  $cap?: CheerioElement,
  withColon: boolean = true
) => {
  if (!$cap?.length || !resolveLabel) return '';
  const kind = $cap.attr('data-bookmark-kind') as OutRefBookmarkKind;
  const idx = $cap.attr('data-index');
  return `${resolveLabel(kind)} ${idx}${withColon ? ':' : ''}`;
};

// MVP solution for captions' i18n based on document's 'language' custom field
const resolveCaptionLabelDefaultFe = (kind: OutRefBookmarkKind) =>
  kind === OutRefBookmarkKind.TABLE ? 'Table' : kind === OutRefBookmarkKind.FIGURE ? 'Figure' : 'Unknown';
const getLanguageCustomFieldFe = (version: TDraft | TVersion): string | undefined => {
  const value = version.customFieldValues?.find((value) => value.customField === 'language')?.value;
  if (value && ECustomFieldType.DROPDOWN in value) return value[ECustomFieldType.DROPDOWN];
  return undefined;
};

export const getResolveCaptionLabelFe = (version: TDraft | TVersion | undefined) => {
  if (!version) return resolveCaptionLabelDefaultFe;
  const language = getLanguageCustomFieldFe(version);
  return language
    ? (kind: OutRefBookmarkKind) => captionLabelMap[language]?.[kind] ?? resolveCaptionLabelDefaultFe(kind)
    : resolveCaptionLabelDefaultFe;
};
